import React from 'react';
import MathContentLayer from '../math/MathContentLayer';

const toNumber = (value, fallback = 0) => {
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : fallback;
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const isSingleLatexFormula = (value) => {
	const text = String(value || '').trim();
	if (!text) return false;

	return (
		/^\$\$[\s\S]+\$\$$/.test(text)
		|| /^\$[^\n$]+\$$/.test(text)
		|| /^\\\([\s\S]+\\\)$/.test(text)
		|| /^\\\[[\s\S]+\\\]$/.test(text)
	);
};

const MindMapTrait = ({
	start = { x: 0, y: 0 },
	end = { x: 100, y: 0 },
	color = 'rgba(109, 40, 217, 0.5)',
	strokeWidth = 1,
	opacity = 1,
	dashArray,
	lineCap = 'round',
	lineJoin = 'round',
	useArcWhenDifferentY = true,
	curvature = 0.5,
	markerEnd,
	className = '',
	style,
	...props
}) => {
	const x1 = toNumber(start?.x);
	const y1 = toNumber(start?.y);
	const x2 = toNumber(end?.x);
	const y2 = toNumber(end?.y);

	// Calculate bounding box for SVG viewBox
	const minX = Math.min(x1, x2);
	const minY = Math.min(y1, y2);
	const maxX = Math.max(x1, x2);
	const maxY = Math.max(y1, y2);
	
	// Add padding to the viewBox to ensure curves aren't cut off
	const padding = 50;
	const viewBoxX = minX - padding;
	const viewBoxY = minY - padding;
	const viewBoxWidth = maxX - minX + padding * 2;
	const viewBoxHeight = maxY - minY + padding * 2;

	// Build path with smooth curves
	const buildPath = () => {
		const distanceX = Math.abs(x2 - x1);
		const distanceY = Math.abs(y2 - y1);
		
		// Subtle curves like NotebookLM - minimal curvature for clean look
		// Horizontal offset creates gentle arc, vertical is minimal
		const horizontalOffset = distanceX * 0.15;
		const verticalOffset = distanceY * 0.08;
		
		// Control points create natural, subtle flowing curves
		const c1x = x1 + horizontalOffset;
		const c1y = y1 + verticalOffset;
		
		const c2x = x2 - horizontalOffset;
		const c2y = y2 - verticalOffset;
		
		return `M ${x1} ${y1} C ${c1x} ${c1y}, ${c2x} ${c2y}, ${x2} ${y2}`;
	};

	const path = buildPath();

	return (
		<svg
			className={className}
			style={{
				position: 'absolute',
				left: 0,
				top: 0,
				width: '100%',
				height: '100%',
				overflow: 'visible',
				pointerEvents: 'none',
				...style,
			}}
			viewBox={`${viewBoxX} ${viewBoxY} ${viewBoxWidth} ${viewBoxHeight}`}
			preserveAspectRatio="none"
			{...props}
		>
			<path
				d={path}
				fill="none"
				stroke={color}
			strokeWidth={strokeWidth * 2}
				opacity={opacity}
				strokeDasharray={dashArray}
				strokeLinecap={lineCap}
				strokeLinejoin={lineJoin}
				markerEnd={markerEnd}
			/>
		</svg>
	);
};

const MindMapElem = ({
	label = 'Mind Map Node',
	position = { x: 0, y: 0 },
	width = 180,
	height = 64,
	backgroundColor = '#1E1E1E',
	borderColor = '#2A2A2A',
	borderWidth = 0,
	borderRadius = 14,
	textColor = '#F5F5F5',
	fontSize = 15,
	fontWeight = 600,
	padding = '12px 16px',
	boxShadow = '0 6px 18px rgba(0, 0, 0, 0.18)',
	align = 'center',
	className = '',
	style,
	children,
	...props
}) => {
	const x = toNumber(position?.x);
	const y = toNumber(position?.y);

	return (
		<div
			className={className}
			style={{
				position: 'absolute',
				left: x,
				top: y,
				width,
				height,
				display: 'flex',
				alignItems: 'center',
				justifyContent:
					align === 'left' ? 'flex-start' : align === 'right' ? 'flex-end' : 'center',
				textAlign: align,
				padding,
				backgroundColor,
					border: borderWidth > 0 ? `${borderWidth}px solid ${borderColor}` : 'none',
				borderRadius,
				color: textColor,
				fontSize,
				fontWeight,
				boxShadow,
				fontFamily: 'Inter, sans-serif',
				userSelect: 'none',
				...style,
			}}
			{...props}
		>
			{children || label}
		</div>
	);
};

const MindMapContentBlock = React.forwardRef(({
	text = 'Mind map content',
	position = { x: 0, y: 0 },
	autoSize = true,
	width,
	height,
	minWidth = 120,
	maxWidth = 420,
	minHeight = 44,
	maxHeight,
	padding = '10px 14px',
	backgroundColor = '#1E1E1E',
	borderColor = '#2A2A2A',
	borderWidth = 0,
	borderRadius = 12,
	textColor = '#F5F5F5',
	fontSize = 14,
	fontWeight = 500,
	lineHeight = 1.35,
	boxShadow = '0 5px 14px rgba(0, 0, 0, 0.16)',
	textAlign = 'center',
	whiteSpace = 'normal',
	overflowWrap = 'anywhere',
	className = '',
	style,
	children,
	...props
}, ref) => {
	const x = toNumber(position?.x);
	const y = toNumber(position?.y);
	const isFormulaOnlyNode = isSingleLatexFormula(text);
	const effectiveFontSize = isFormulaOnlyNode ? Number(fontSize) * 1.5 : fontSize;
	const effectiveMaxWidth = isFormulaOnlyNode ? undefined : maxWidth;
	const effectiveWhiteSpace = isFormulaOnlyNode ? 'nowrap' : whiteSpace;
	const effectiveOverflowWrap = isFormulaOnlyNode ? 'normal' : overflowWrap;
	const effectiveWordBreak = isFormulaOnlyNode ? 'normal' : 'break-word';

	const computedWidth = width ?? (autoSize ? 'fit-content' : 180);
	const computedHeight = autoSize ? 'auto' : (height ?? 'auto');

	return (
		<div
			ref={ref}
			className={className}
			style={{
				position: 'absolute',
				left: x,
				top: y,
				display: 'inline-flex',
				alignItems: 'center',
				justifyContent: 'center',
				width: computedWidth,
				height: computedHeight,
				minWidth,
				maxWidth: effectiveMaxWidth,
				minHeight,
				maxHeight,
				padding,
				backgroundColor,
				border: `${borderWidth}px solid ${borderColor}`,
				borderRadius,
				boxShadow,
				color: textColor,
				fontSize: effectiveFontSize,
				fontWeight,
				lineHeight,
				textAlign,
				fontFamily: 'Inter, sans-serif',
				whiteSpace: effectiveWhiteSpace,
				overflowWrap: effectiveOverflowWrap,
				wordBreak: effectiveWordBreak,
				userSelect: 'none',
				transition: 'all 0.2s ease',
				...style,
			}}
			{...props}
		>
			{children || (
				<MathContentLayer
					content={text}
					style={{
						width: '100%',
						maxWidth: isFormulaOnlyNode ? 'none' : '100%',
						textAlign,
						fontSize: effectiveFontSize,
						whiteSpace: isFormulaOnlyNode ? 'nowrap' : 'normal',
						overflowWrap: isFormulaOnlyNode ? 'normal' : 'break-word',
						wordBreak: isFormulaOnlyNode ? 'normal' : 'break-word',
					}}
				/>
			)}
		</div>
	);
});

const MindMapExpandCollapseButton = ({
	status = 'expand',
	size = 30,
	width,
	height,
	position,
	onClick,
	disabled = false,
	backgroundColor = '#1E1E1E',
	hoverBackgroundColor = '#2A2A2A',
	borderColor = '#2A2A2A',
	borderWidth = 0,
	borderRadius = '999px',
	iconColor = '#F5F5F5',
	iconStrokeWidth = 2.2,
	padding = 0,
	boxShadow = '0 4px 12px rgba(0, 0, 0, 0.16)',
	focusRingColor = 'rgba(109, 40, 217, 0.35)',
	focusRingWidth = 3,
	transition = 'all 0.2s ease',
	className = '',
	style,
	...props
}) => {
	const [isHovered, setIsHovered] = React.useState(false);
	const [isFocused, setIsFocused] = React.useState(false);

	const buttonWidth = width ?? size;
	const buttonHeight = height ?? size;
	const arrowDirection = status === 'collapse' ? 'left' : 'right';
	const x = position ? toNumber(position?.x) : undefined;
	const y = position ? toNumber(position?.y) : undefined;

	const handleKeyDown = (event) => {
		if (disabled) return;
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			onClick?.(event);
		}
	};

	return (
		<button
			type="button"
			onClick={disabled ? undefined : onClick}
			onKeyDown={handleKeyDown}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
			onFocus={() => setIsFocused(true)}
			onBlur={() => setIsFocused(false)}
			disabled={disabled}
			aria-label={status === 'collapse' ? 'Collapse mind map branch' : 'Expand mind map branch'}
			className={className}
			style={{
				position: position ? 'absolute' : 'relative',
				left: x,
				top: y,
				width: buttonWidth,
				height: buttonHeight,
				display: 'inline-flex',
				alignItems: 'center',
				justifyContent: 'center',
				padding,
				border: `${borderWidth}px solid ${borderColor}`,
				borderRadius,
				backgroundColor: isHovered && !disabled ? hoverBackgroundColor : backgroundColor,
				color: iconColor,
				cursor: disabled ? 'not-allowed' : 'pointer',
				opacity: disabled ? 0.6 : 1,
				outline: 'none',
				transition,

				boxShadow,
				...style,
			}}
			{...props}
		>
			<svg
				width="58%"
				height="58%"
				viewBox="0 0 24 24"
				fill="none"
				aria-hidden="true"
			>
				<path
					d={
						arrowDirection === 'left'
							? 'M14.5 5.5L8 12l6.5 6.5'
							: 'M9.5 5.5L16 12l-6.5 6.5'
					}
					stroke={iconColor}
					strokeWidth={iconStrokeWidth}
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
			</svg>
		</button>
	);
};

const MindMapActionButton = React.forwardRef(({
	text = 'Click me',
	position = { x: 0, y: 0 },
	onClick,
	autoSize = true,
	width,
	height,
	minWidth = 120,
	maxWidth = 420,
	minHeight = 44,
	maxHeight,
	padding = '10px 14px',
	backgroundColor = '#1E1E1E',
	hoverBackgroundColor = '#2A2A2A',
	activeBackgroundColor = '#1A1A1A',
	borderColor = '#2A2A2A',
		borderWidth = 0,
	borderRadius = 12,
	textColor = '#F5F5F5',
	fontSize = 14,
	fontWeight = 500,
	lineHeight = 1.35,
	boxShadow = '0 5px 14px rgba(0, 0, 0, 0.16)',
	textAlign = 'center',
	whiteSpace = 'normal',
	overflowWrap = 'anywhere',
	disabled = false,
	transition = 'all 0.2s ease',
	focusRingColor = 'rgba(109, 40, 217, 0.35)',
	focusRingWidth = 3,
	className = '',
	style,
	children,
	...props
}, ref) => {
	const x = toNumber(position?.x);
	const y = toNumber(position?.y);
	const isFormulaOnlyNode = isSingleLatexFormula(text);
	const effectiveFontSize = isFormulaOnlyNode ? Number(fontSize) * 1.5 : fontSize;
	const effectiveMaxWidth = isFormulaOnlyNode ? undefined : maxWidth;
	const effectiveWhiteSpace = isFormulaOnlyNode ? 'nowrap' : whiteSpace;
	const effectiveOverflowWrap = isFormulaOnlyNode ? 'normal' : overflowWrap;
	const effectiveWordBreak = isFormulaOnlyNode ? 'normal' : 'break-word';
	const [isHovered, setIsHovered] = React.useState(false);
	const [isActive, setIsActive] = React.useState(false);
	const [isFocused, setIsFocused] = React.useState(false);

	const computedWidth = width ?? (autoSize ? 'fit-content' : 180);
	const computedHeight = autoSize ? 'auto' : (height ?? 'auto');

	const currentBackgroundColor =
		isActive && !disabled
			? activeBackgroundColor
			: isHovered && !disabled
			? hoverBackgroundColor
			: backgroundColor;

	const handleKeyDown = (event) => {
		if (disabled) return;
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			onClick?.(event);
		}
	};

	return (
		<button
			ref={ref}
			type="button"
			onClick={disabled ? undefined : onClick}
			onKeyDown={handleKeyDown}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
			onMouseDown={() => setIsActive(true)}
			onMouseUp={() => setIsActive(false)}
			onFocus={() => setIsFocused(true)}
			onBlur={() => setIsFocused(false)}
			disabled={disabled}
			className={className}
			style={{
				position: 'absolute',
				left: x,
				top: y,
				display: 'inline-flex',
				alignItems: 'center',
				justifyContent: 'center',
				width: computedWidth,
				height: computedHeight,
				minWidth,
				maxWidth: effectiveMaxWidth,
				minHeight,
				maxHeight,
				padding,
				backgroundColor: currentBackgroundColor,
					border: borderWidth > 0 ? `${borderWidth}px solid ${borderColor}` : 'none',
				borderRadius,
				boxShadow,
				color: textColor,
				fontSize: effectiveFontSize,
				fontWeight,
				lineHeight,
				textAlign,
				fontFamily: 'Inter, sans-serif',
				whiteSpace: effectiveWhiteSpace,
				overflowWrap: effectiveOverflowWrap,
				wordBreak: effectiveWordBreak,
				userSelect: 'none',
				cursor: disabled ? 'not-allowed' : 'pointer',
				opacity: disabled ? 0.6 : 1,
				outline: 'none',
				transition,
				transform: isHovered && !disabled ? 'translateY(-1px)' : 'translateY(0)',
				...(isFocused
					? {
						boxShadow: `${boxShadow}, 0 0 0 ${focusRingWidth}px ${focusRingColor}`,
					  }
					: null),
				...style,
			}}
			{...props}
		>
			{children || (
				<MathContentLayer
					content={text}
					style={{
						width: '100%',
						maxWidth: isFormulaOnlyNode ? 'none' : '100%',
						textAlign,
						fontSize: effectiveFontSize,
						whiteSpace: isFormulaOnlyNode ? 'nowrap' : 'normal',
						overflowWrap: isFormulaOnlyNode ? 'normal' : 'break-word',
						wordBreak: isFormulaOnlyNode ? 'normal' : 'break-word',
					}}
				/>
			)}
		</button>
	);
});

export default MindMapElem;
export {
	MindMapElem,
	MindMapTrait,
	MindMapContentBlock,
	MindMapExpandCollapseButton,
	MindMapActionButton,
};
